import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Block as SuspendIcon,
  PlayArrow as ReactivateIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as VerifyIcon,
  Cancel as RejectIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import platformService from '../../services/platformService';
import usePlatformAuth from '../../hooks/usePlatformAuth';


export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDealer, isSuperAdmin } = usePlatformAuth();

  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Feature modules
  const [modules, setModules] = useState([]);
  const [togglingModule, setTogglingModule] = useState(null);

  // Dialogs
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendLoading, setSuspendLoading] = useState(false);

  // Verification
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'user'
  });
  const [userLoading, setUserLoading] = useState(false);

  // Fetch tenant details
  const fetchTenant = async () => {
    try {
      const response = await platformService.getTenant(id);
      if (response.success) {
        setTenant(response.data.tenant);
      }
    } catch (err) {
      setError('Failed to load tenant details');
      console.error('Fetch tenant error:', err);
    }
  };

  // Fetch tenant users
  const fetchUsers = async () => {
    try {
      const response = await platformService.getTenantUsers(id);
      if (response.success) {
        setUsers(response.data.users);
      }
    } catch (err) {
      console.error('Fetch users error:', err);
    }
  };

  // Fetch tenant feature modules
  const fetchModules = async () => {
    try {
      const response = await platformService.getTenantModules(id);
      if (response.success) {
        setModules(response.data.modules);
      }
    } catch (err) {
      console.error('Fetch modules error:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTenant(), fetchUsers(), fetchModules()]);
      setLoading(false);
    };
    loadData();
  }, [id]);

  const handleToggleModule = async (moduleKey, enabled) => {
    setTogglingModule(moduleKey);
    // optimistic update
    setModules((prev) => prev.map((m) => (m.key === moduleKey ? { ...m, enabled } : m)));
    try {
      const response = await platformService.setTenantModule(id, moduleKey, enabled);
      if (response.success) {
        toast.success(`${moduleKey.toUpperCase()} ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (err) {
      // revert on failure
      setModules((prev) => prev.map((m) => (m.key === moduleKey ? { ...m, enabled: !enabled } : m)));
      toast.error(err.response?.data?.message || 'Failed to update module');
    } finally {
      setTogglingModule(null);
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    setSuspendLoading(true);
    try {
      const response = await platformService.suspendTenant(id, suspendReason);
      if (response.success) {
        toast.success('Tenant suspended');
        setTenant(response.data.tenant);
        setSuspendDialogOpen(false);
        setSuspendReason('');
      }
    } catch (err) {
      toast.error('Failed to suspend tenant');
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      const response = await platformService.reactivateTenant(id);
      if (response.success) {
        toast.success('Tenant reactivated');
        setTenant(response.data.tenant);
      }
    } catch (err) {
      toast.error('Failed to reactivate tenant');
    }
  };

  const handleVerify = async () => {
    if (!window.confirm(`Verify "${tenant.company_name}"? They will be able to access the system.`)) {
      return;
    }

    setVerifyLoading(true);
    try {
      const response = await platformService.verifyTenant(id);
      if (response.success) {
        toast.success('Tenant verified successfully');
        setTenant(response.data.tenant);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to verify tenant';
      toast.error(message);
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setRejectLoading(true);
    try {
      const response = await platformService.rejectTenant(id, rejectReason);
      if (response.success) {
        toast.success('Tenant rejected');
        setTenant(response.data.tenant);
        setRejectDialogOpen(false);
        setRejectReason('');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to reject tenant';
      toast.error(message);
    } finally {
      setRejectLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setUserLoading(true);
    try {
      await platformService.createTenantUser(id, userForm);
      toast.success('User created. Login credentials sent to their email.');
      setUserDialogOpen(false);
      setUserForm({ firstName: '', lastName: '', email: '', role: 'user' });
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create user';
      toast.error(message);
    } finally {
      setUserLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await platformService.deleteTenantUser(id, userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tenant) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/tenants')} sx={{ mb: 2 }}>
          Back to Tenants
        </Button>
        <Alert severity="error">{error || 'Tenant not found'}</Alert>
      </Box>
    );
  }

  const userPercentage = tenant.max_users === -1
    ? 0
    : (tenant.userCount / tenant.max_users) * 100;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/tenants')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' } }}>
            {tenant.company_name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={tenant.is_active ? 'Active' : 'Pending Verification'}
              size="small"
              color={tenant.is_active ? 'success' : 'warning'}
            />
          </Box>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' }, flexWrap: 'wrap' }}>
          {isSuperAdmin() && !tenant.is_active && (
            <Button
              variant="contained"
              color="success"
              startIcon={verifyLoading ? <CircularProgress size={16} color="inherit" /> : <VerifyIcon />}
              onClick={handleVerify}
              disabled={verifyLoading}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Verify
            </Button>
          )}
          {isSuperAdmin() && !tenant.is_active && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              onClick={() => setRejectDialogOpen(true)}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Reject
            </Button>
          )}
          {tenant.is_active ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<SuspendIcon />}
              onClick={() => setSuspendDialogOpen(true)}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Suspend
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<ReactivateIcon />}
              onClick={handleReactivate}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Reactivate
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Company Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Company Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Billing Email
                  </Typography>
                  <Typography>{tenant.billing_email || '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography>{new Date(tenant.created_at).toLocaleString()}</Typography>
                </Box>
                {tenant.suspended_at && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Suspended
                    </Typography>
                    <Typography color="error">
                      {new Date(tenant.suspended_at).toLocaleString()}
                      {tenant.suspended_reason && ` - ${tenant.suspended_reason}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Usage
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Users</Typography>
                  <Typography variant="body2">
                    {tenant.userCount} / {tenant.max_users === -1 ? 'Unlimited' : tenant.max_users}
                  </Typography>
                </Box>
                {tenant.max_users !== -1 && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(userPercentage, 100)}
                    color={userPercentage >= 90 ? 'error' : userPercentage >= 70 ? 'warning' : 'primary'}
                  />
                )}
              </Box>
              {tenant.trial_ends_at && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Trial Ends
                  </Typography>
                  <Typography>
                    {new Date(tenant.trial_ends_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Feature Modules */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Feature Modules
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enable optional modules for this agency.
              </Typography>
              {modules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No toggleable modules available.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {modules.map((m) => (
                    <Grid item xs={12} sm={6} md={4} key={m.key}>
                      <Box
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 2,
                          p: 2,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {m.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={m.enabled ? 'Enabled' : 'Disabled'}
                              color={m.enabled ? 'success' : 'default'}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {m.description}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          sx={{ mt: 1 }}
                          control={
                            <Switch
                              checked={m.enabled}
                              disabled={!isSuperAdmin() || togglingModule === m.key}
                              onChange={(e) => handleToggleModule(m.key, e.target.checked)}
                            />
                          }
                          label={togglingModule === m.key ? 'Saving…' : m.enabled ? 'On' : 'Off'}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
              {!isSuperAdmin() && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Only super admins can change module access.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Users */}
        {isSuperAdmin() && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Users</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setUserDialogOpen(true)}
                  >
                    Add User
                  </Button>
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: { xs: 350, sm: 500 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No users found
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              {user.first_name} {user.last_name}
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{user.email}</TableCell>
                            <TableCell>
                              <Chip label={user.role} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              <Chip
                                label={user.is_active ? 'Active' : 'Inactive'}
                                size="small"
                                color={user.is_active ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)}>
        <DialogTitle>Suspend Tenant</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to suspend {tenant.company_name}?
          </Typography>
          <TextField
            fullWidth
            label="Reason"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSuspend}
            disabled={suspendLoading}
          >
            {suspendLoading ? <CircularProgress size={20} /> : 'Suspend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={userForm.firstName}
                onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={userForm.lastName}
                onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                required
                helperText="Login credentials will be sent to this email"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={userLoading}
          >
            {userLoading ? <CircularProgress size={20} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Company</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject <strong>{tenant?.company_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejection. This may be visible to the company.
          </Typography>
          <TextField
            fullWidth
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={3}
            required
            placeholder="e.g., Incomplete information, Invalid business details..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejectLoading || !rejectReason.trim()}
          >
            {rejectLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
